import { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { useDarkModeCheck } from "../DashboardLayout";
import ApiConnectionsUI from "../../../ui/dashboard/api-connections/ApiConnectionsUI";
import { GET_USER_INFO } from "../../../service/graphql/query";
import { logout } from "../../../service/graphql/utils";
import {
  CREATE_API_CONNECTION,
  TEST_CONNECTION,
} from "../../../service/graphql/connections";
import { validateUUID } from "../../../service/utils/functions";

const ApiConnections = () => {
  const navigate = useNavigate();
  const { darkMode } = useDarkModeCheck();

  const [clientName, setClientName] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [weightronUid, setWeightronUid] = useState("");
  const [weightronSecret, setWeightronSecret] = useState("");
  const [weightronApiKey, setWeightronApiKey] = useState("");

  const [invalidClientName, setInvalidClientName] = useState<string>();
  const [invalidApplicationId, setInvalidApplicationId] = useState<string>();
  const [invalidWeightronUid, setInvalidWeightronUid] = useState<string>();
  const [invalidWeightronSecret, setInvalidWeightronSecret] =
    useState<string>();
  const [invalidWeightronApiKey, setInvalidWeightronApiKey] =
    useState<string>();

  const [connectionInProgress, setConnectionInProgress] = useState(false);
  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");

  const [getUserInfo] = useLazyQuery(GET_USER_INFO, {
    errorPolicy: "all",
    onError: () => navigate("/login"),
  });
  const [createApiConnection] = useMutation(CREATE_API_CONNECTION, {
    onError: (err: any) => {
      setSubmissionInProgress(false);
      setIsSuccess(false);
      setSubmitMsg(err.message);
    },
    errorPolicy: "all",
  });
  const [testConnection] = useMutation(TEST_CONNECTION, {
    onError: (err: any) => {
      setConnectionInProgress(false);
      setIsSuccess(false);
      setSubmitMsg(err.message);
    },
    errorPolicy: "all",
  });

  useEffect(() => {
    const userData = getUserInfo({ context: { apiName: "middleware" } });
    Promise.all([userData]).then((res) => {
      const user = res[0].data?.getCustomers[0];
      if (user) {
        if (user.category === "Tipping" || !user.pageAuth.weightingPage) {
          window.history.replaceState(null, "", "/");
          navigate("/dashboard/home");
        }
      } else logout(navigate);
    });
  }, [getUserInfo, navigate]);

  const resetInvalidMsg = useCallback(() => {
    setInvalidClientName(undefined);
    setInvalidApplicationId(undefined);
    setInvalidWeightronSecret(undefined);
    setInvalidWeightronUid(undefined);
    setInvalidWeightronApiKey(undefined);
    setSubmitMsg("");
    setIsSuccess(false);
  }, []);

  const validateCheck = useCallback(() => {
    let isValid = true;
    if (!clientName || clientName.length <= 0) {
      setInvalidClientName("Client name is required.");
      isValid = false;
    }
    if (!applicationId || applicationId.length <= 0) {
      setInvalidApplicationId("Application ID is required.");
      isValid = false;
    }
    if (!weightronSecret || weightronSecret.length <= 0) {
      setInvalidWeightronSecret("Weightron API Secret is required.");
      isValid = false;
    }
    if (!weightronUid || weightronUid.length <= 0) {
      setInvalidWeightronUid("Weightron UID is required.");
      isValid = false;
    }
    if (weightronUid && validateUUID(weightronUid) === false) {
      setInvalidWeightronUid("Weightron UID is not valid.");
      isValid = false;
    }
    if (!weightronApiKey || weightronApiKey.length <= 0) {
      setInvalidWeightronApiKey("Weightron API Key is required.");
      isValid = false;
    }
    return isValid;
  }, [
    applicationId,
    clientName,
    weightronApiKey,
    weightronSecret,
    weightronUid,
  ]);

  const onEvaluateConnection = useCallback(async () => {
    resetInvalidMsg();
    setConnectionInProgress(true);

    const isValid = validateCheck();
    if (!isValid) {
      setConnectionInProgress(false);
      return;
    }

    const testInput = {
      name: clientName,
      application_id: applicationId,
      weightron_uid: weightronUid,
      weightron_secret: weightronSecret,
      weightron_key: weightronApiKey,
    };
    const res = await testConnection({
      variables: { data: testInput },
      context: { apiName: "cms" },
    });
    if (res && res.data && res.data.testConnection) {
      const { status, message } = res.data.testConnection;
      setIsSuccess(status === "OK");
      setSubmitMsg(message);
      setConnectionInProgress(false);
    }
  }, [
    resetInvalidMsg,
    validateCheck,
    clientName,
    applicationId,
    weightronSecret,
    weightronUid,
    weightronApiKey,
    testConnection,
  ]);

  const onSave = useCallback(async () => {
    resetInvalidMsg();
    setSubmissionInProgress(true);

    // Validation section
    const isValid = validateCheck();
    if (!isValid) {
      setSubmissionInProgress(false);
      return;
    }

    const createInput = {
      name: clientName,
      application_id: applicationId,
      weightron_uid: weightronUid,
      weightron_secret: weightronSecret,
      weightron_key: weightronApiKey,
    };
    const res = await createApiConnection({
      variables: { input: createInput },
      context: { apiName: "cms" },
    });
    if (res && res.data && res.data.createApiConnection) {
      setIsSuccess(true);
      setSubmitMsg("API Connection created successfully");
      setSubmissionInProgress(false);
    }
  }, [
    resetInvalidMsg,
    validateCheck,
    clientName,
    applicationId,
    weightronSecret,
    weightronUid,
    weightronApiKey,
    createApiConnection,
  ]);

  const connectionsPageProps = useMemo(
    () => ({
      clientName,
      applicationId,
      weightronUid,
      weightronSecret,
      weightronApiKey,
      connectionInProgress,
      submissionInProgress,
      isSuccess,
      submitMsg,
      invalidClientName,
      invalidApplicationId,
      invalidWeightronUid,
      invalidWeightronSecret,
      invalidWeightronApiKey,
      setClientName,
      setApplicationId,
      setWeightronUid,
      setWeightronSecret,
      setWeightronApiKey,
      onEvaluateConnection,
      onSave,
      resetInvalidMsg,
      darkMode,
    }),
    [
      clientName,
      applicationId,
      weightronSecret,
      weightronUid,
      weightronApiKey,
      connectionInProgress,
      submissionInProgress,
      isSuccess,
      submitMsg,
      invalidClientName,
      invalidApplicationId,
      invalidWeightronSecret,
      invalidWeightronUid,
      invalidWeightronApiKey,
      setClientName,
      setApplicationId,
      setWeightronSecret,
      setWeightronUid,
      setWeightronApiKey,
      onEvaluateConnection,
      onSave,
      resetInvalidMsg,
      darkMode,
    ]
  );

  return <ApiConnectionsUI {...connectionsPageProps} />;
};

export default ApiConnections;
